import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Package, Trophy, Store, Euro, Check, X, PieChart, Truck, Box, LayoutGrid, Pizza, Settings, CalendarRange } from "lucide-react";
import api, { apiErr, FILE_BASE } from "@/lib/api";
import { DashboardLayout } from "@/components/DashboardLayout";
import CatalogManager from "@/pages/dashboard/CatalogManager";
import SettingsPanel from "@/pages/dashboard/SettingsPanel";
import SupplyProductsManager from "@/pages/dashboard/SupplyProductsManager";
import PizzeriaManager from "@/pages/dashboard/PizzeriaManager";
import EditionsManager from "@/pages/dashboard/EditionsManager";

const STATUS_LABELS = { en_attente_paiement: "Attente paiement", recue: "Reçue", en_preparation: "En préparation", en_livraison: "En livraison", livree: "Livrée" };
const badgeCls = (st) => st === "approved" ? "bg-green-100 text-green-700" : st === "rejected" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700";
const stLabel = (st) => st === "approved" ? "Validé" : st === "rejected" ? "Rejeté" : "En attente";

const SECTIONS = [
  { key: "editions", label: "Éditions", icon: CalendarRange },
  { key: "candidates", label: "Modération jeux", icon: Trophy },
  { key: "budgets", label: "Modération cocardes", icon: PieChart },
  { key: "orders", label: "Commandes", icon: Package },
  { key: "pizzerias", label: "Pizzerias", icon: Store },
  { key: "supply", label: "Commandes PLV", icon: Truck },
  { key: "products", label: "Boîtes & Kit", icon: Box },
  { key: "boxes", label: "Modèles de boîtes", icon: LayoutGrid },
  { key: "carte", label: "Carte pizzas", icon: Pizza },
  { key: "settings", label: "Réglages", icon: Settings },
];

function BudgetsModeration() {
  const [budgets, setBudgets] = useState([]);
  const load = () => api.get("/admin/budgets").then((r) => setBudgets(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const moderate = async (id, status) => {
    try { await api.put(`/admin/budgets/${id}/moderate`, { status }); toast.success(status === "approved" ? "Cocarde validée" : "Cocarde rejetée"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
      {budgets.map((b) => (
        <div key={b.id} data-testid={`admin-budget-${b.id}`} className="card-cocarde p-4">
          <div className="flex items-center gap-3">
            {b.logo_url ? <img src={`${FILE_BASE}${b.logo_url}`} alt="" className="w-12 h-12 rounded-full object-cover border border-slate-200 shrink-0" />
              : <div className="w-12 h-12 rounded-full rosette shrink-0" />}
            <div className="min-w-0 flex-1">
              <p className="font-display text-lg cc-navy truncate">{b.title}</p>
              <p className="text-slate-400 text-xs">par {b.author_name} · {b.theme}</p>
            </div>
            <span className={`text-xs font-bold rounded-full px-2 py-0.5 shrink-0 ${badgeCls(b.status)}`}>{stLabel(b.status)}</span>
          </div>
          <p className="text-slate-500 text-xs mt-2 truncate">{b.items.map((i) => `${i.label} ${i.percent}%`).join(" · ")}</p>
          <div className="flex gap-2 mt-3">
            <button data-testid={`admin-budget-approve-${b.id}`} onClick={() => moderate(b.id, "approved")} disabled={b.status === "approved"} className="flex-1 bg-green-500 text-white rounded-full py-1.5 text-sm font-bold inline-flex items-center justify-center gap-1 disabled:opacity-40"><Check size={14} /> Valider</button>
            <button data-testid={`admin-budget-reject-${b.id}`} onClick={() => moderate(b.id, "rejected")} disabled={b.status === "rejected"} className="flex-1 bg-red-500 text-white rounded-full py-1.5 text-sm font-bold inline-flex items-center justify-center gap-1 disabled:opacity-40"><X size={14} /> Rejeter</button>
          </div>
        </div>
      ))}
      {budgets.length === 0 && <p className="text-slate-400">Aucune cocarde soumise.</p>}
    </div>
  );
}

export default function AdminDashboard({ user }) {
  const [tab, setTab] = useState("candidates");
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [pizzerias, setPizzerias] = useState([]);
  const [supply, setSupply] = useState([]);
  const [edFilter, setEdFilter] = useState("all");
  const [edNames, setEdNames] = useState({});
  const edLabel = (s) => edNames[s] || s;

  const loadAll = () => {
    api.get("/admin/stats").then((r) => setStats(r.data));
    api.get("/admin/orders").then((r) => setOrders(r.data));
    api.get("/admin/candidates").then((r) => setCandidates(r.data));
    api.get("/admin/pizzerias").then((r) => setPizzerias(r.data));
    api.get("/admin/supply/orders").then((r) => setSupply(r.data));
    api.get("/editions").then((r) => setEdNames(Object.fromEntries(r.data.map((e) => [e.slug, e.name]))));
  };
  useEffect(loadAll, []);

  const moderate = async (id, status) => {
    try { await api.put(`/admin/candidates/${id}/moderate`, { status }); toast.success(status === "approved" ? "Candidat validé" : "Candidat rejeté"); loadAll(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const toggleDemo = async () => {
    try { const { data } = await api.put("/admin/candidates/demo/toggle"); toast.success(data.hidden ? `${data.count} candidats démo masqués` : `${data.count} candidats démo affichés`); loadAll(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const togglePiz = async (id) => { try { await api.put(`/admin/pizzerias/${id}/toggle`); loadAll(); } catch (e) { toast.error(apiErr(e)); } };
  const setSupplyStatus = async (id, status) => { try { await api.put(`/admin/supply/orders/${id}/status`, { status }); loadAll(); } catch (e) { toast.error(apiErr(e)); } };

  const editions = [...new Set(candidates.map((c) => c.edition || "elections-2027"))];
  const shownCandidates = edFilter === "all" ? candidates : candidates.filter((c) => (c.edition || "elections-2027") === edFilter);

  const stat = [
    { icon: Euro, l: "Chiffre d'affaires", v: `${stats?.revenue ?? 0} €` },
    { icon: Package, l: "Commandes payées", v: stats?.orders_paid ?? 0 },
    { icon: Trophy, l: "Candidats à valider", v: stats?.candidates_pending ?? 0 },
    { icon: Store, l: "Pizzerias", v: stats?.pizzerias ?? 0 },
  ];

  return (
    <DashboardLayout title="Startup COCARDE" subtitle={`Bonjour ${user?.name || ""}`} sections={SECTIONS} active={tab} onSelect={setTab}>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stat.map((s, i) => (
          <div key={i} data-testid={`admin-stat-${i}`} className="card-cocarde p-5">
            <s.icon className="text-cc-red" size={22} />
            <p className="font-display text-3xl cc-navy mt-2">{s.v}</p>
            <p className="text-slate-500 text-sm">{s.l}</p>
          </div>
        ))}
      </div>

      <h2 className="font-display text-2xl cc-navy mt-8 mb-4">{SECTIONS.find((s) => s.key === tab)?.label}</h2>

      {tab === "candidates" && (
        <div>
          <div className="mb-4 flex justify-between items-center flex-wrap gap-2">
            <select data-testid="admin-edition-filter" value={edFilter} onChange={(e) => setEdFilter(e.target.value)} className="border border-slate-300 rounded-full px-4 py-2 text-sm font-semibold cc-navy">
              <option value="all">Toutes les éditions</option>
              {editions.map((e) => <option key={e} value={e}>{edLabel(e)}</option>)}
            </select>
            <button data-testid="admin-toggle-demo" onClick={toggleDemo} className="btn-navy text-sm py-2 px-4">Masquer / Afficher les candidats démo</button>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {shownCandidates.map((c) => (
              <div key={c.id} data-testid={`admin-candidate-${c.id}`} className="card-cocarde overflow-hidden">
                <div className="aspect-video bg-black">
                  {c.video_url && <video src={`${FILE_BASE}${c.video_url}`} poster={c.poster_url ? `${FILE_BASE}${c.poster_url}` : undefined} preload="metadata" controls className="w-full h-full object-cover" />}
                </div>
                <div className="p-4">
                  <div className="flex justify-between items-center gap-2">
                    <h3 className="font-display text-xl cc-navy truncate">{c.display_name}</h3>
                    <span className={`text-xs font-bold rounded-full px-2 py-0.5 shrink-0 ${badgeCls(c.status)}`}>{stLabel(c.status)}</span>
                  </div>
                  <span className="inline-block mt-1 text-[11px] font-bold bg-cc-navy text-white rounded-full px-2 py-0.5">{edLabel(c.edition)}</span>
                  <p className="text-slate-600 text-sm mt-1">{c.program}</p>
                  <p className="text-slate-400 text-xs mt-1">{c.votes} votes</p>
                  {c.status === "pending" && (
                    <div className="flex gap-2 mt-3">
                      <button data-testid={`admin-approve-${c.id}`} onClick={() => moderate(c.id, "approved")} className="flex-1 bg-green-500 text-white rounded-full py-2 text-sm font-bold inline-flex items-center justify-center gap-1"><Check size={15} /> Valider</button>
                      <button data-testid={`admin-reject-${c.id}`} onClick={() => moderate(c.id, "rejected")} className="flex-1 bg-red-500 text-white rounded-full py-2 text-sm font-bold inline-flex items-center justify-center gap-1"><X size={15} /> Rejeter</button>
                    </div>
                  )}
                </div>
              </div>
            ))}
            {shownCandidates.length === 0 && <p className="text-slate-400">Aucune candidature.</p>}
          </div>
        </div>
      )}

      {tab === "editions" && <EditionsManager />}
      {tab === "budgets" && <BudgetsModeration />}

      {tab === "orders" && (
        <div className="card-cocarde overflow-hidden">
          {orders.map((o) => (
            <div key={o.id} className="flex flex-wrap items-center justify-between gap-2 p-4 border-b border-slate-100">
              <div>
                <p className="font-bold cc-navy">{o.customer_name} · {o.items?.map((i) => `${i.quantity}× ${i.pizza_name} (${i.box_name})`).join(", ")}</p>
                <p className="text-slate-500 text-sm">{o.pizzeria?.name} — {o.quantity}× · {o.total} €</p>
              </div>
              <span className={`text-xs font-bold rounded-full px-3 py-1 ${o.payment_status === "paid" ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}`}>
                {o.payment_status === "paid" ? STATUS_LABELS[o.status] || o.status : "Non payée"}
              </span>
            </div>
          ))}
          {orders.length === 0 && <p className="p-6 text-slate-400">Aucune commande.</p>}
        </div>
      )}

      {tab === "pizzerias" && <PizzeriaManager pizzerias={pizzerias} reload={loadAll} toggle={togglePiz} />}

      {tab === "supply" && (
        <div className="space-y-3">
          {supply.map((o) => (
            <div key={o.id} className="card-cocarde p-4">
              <div className="flex justify-between items-center flex-wrap gap-2">
                <div>
                  <p className="font-bold cc-navy">{o.pizzeria_name} <span className="text-slate-400 font-normal">· {o.owner_name}</span></p>
                  <p className="text-slate-500 text-sm">{o.items.map((i) => `${i.quantity}× ${i.name}`).join(", ")}</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-display text-xl text-cc-red">{o.total} €</span>
                  <select data-testid={`admin-supply-status-${o.id}`} value={o.status} onChange={(e) => setSupplyStatus(o.id, e.target.value)}
                    className="border border-slate-300 rounded-lg px-2 py-1.5 text-sm font-semibold">
                    <option value="en_attente">En attente</option>
                    <option value="confirmee">Confirmée</option>
                    <option value="expediee">Expédiée</option>
                    <option value="livree">Livrée</option>
                  </select>
                </div>
              </div>
            </div>
          ))}
          {supply.length === 0 && <p className="text-slate-400">Aucune commande PLV.</p>}
        </div>
      )}

      {tab === "products" && <SupplyProductsManager />}
      {tab === "boxes" && <CatalogManager type="boxes" />}
      {tab === "carte" && <CatalogManager type="pizzas" />}
      {tab === "settings" && <SettingsPanel />}
    </DashboardLayout>
  );
}
